import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from '../config/api';

export default function Register() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    acceptedTerms: false,
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showMFA, setShowMFA] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post('/auth/register', formData);
      if (response.data.success) {
        setShowMFA(true);
      } else {
        // Map error codes to user-friendly messages
        const errorMessages: Record<string, string> = {
          'INVALID_EMAIL': 'El correo electrónico no es válido',
          'INVALID_PHONE': 'El número de teléfono debe tener 10 dígitos y comenzar con 3',
          'INVALID_PASSWORD': 'La contraseña no cumple con los requisitos de seguridad',
          'DUPLICATE_EMAIL': 'Este correo electrónico ya está registrado',
          'DUPLICATE_PHONE': 'Este número de teléfono ya está registrado',
          'TERMS_NOT_ACCEPTED': 'Debes aceptar el tratamiento de datos personales',
        };
        setError(errorMessages[response.data.error] || response.data.error || 'Error al registrarse');
      }
    } catch (err: any) {
      const errorCode = err.response?.data?.error;
      const errorMessages: Record<string, string> = {
        'INVALID_EMAIL': 'El correo electrónico no es válido',
        'INVALID_PHONE': 'El número de teléfono debe tener 10 dígitos y comenzar con 3',
        'INVALID_PASSWORD': 'La contraseña no cumple con los requisitos de seguridad',
        'DUPLICATE_EMAIL': 'Este correo electrónico ya está registrado',
        'DUPLICATE_PHONE': 'Este número de teléfono ya está registrado',
        'TERMS_NOT_ACCEPTED': 'Debes aceptar el tratamiento de datos personales',
      };
      setError(errorMessages[errorCode] || 'Error al registrarse');
    } finally {
      setLoading(false);
    }
  };

  const handleMFASubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post('/auth/verify-mfa', {
        email: formData.email,
        code: mfaCode,
      });
      if (response.data.valid) {
        navigate('/login');
      } else {
        setError('Código inválido');
      }
    } catch (err: any) {
      setError('Error al verificar código');
    } finally {
      setLoading(false);
    }
  };

  if (showMFA) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
        <div className="max-w-md w-full space-y-8">
          <h2 className="text-center text-3xl font-extrabold text-gray-900">
            Verifica tu correo
          </h2>
          <p className="text-center text-sm text-gray-600">
            Ingresa el código que enviamos a {formData.email}
          </p>
          <form onSubmit={handleMFASubmit} className="mt-8 space-y-6">
            {error && <div className="rounded-md bg-red-50 p-4"><p className="text-sm text-red-800">{error}</p></div>}
            <input
              type="text"
              required
              value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value)}
              className="appearance-none relative block w-full px-3 py-2 border border-gray-300 rounded-md"
              placeholder="Código de 6 dígitos"
            />
            <button
              type="submit"
              disabled={loading}
              className="w-full py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              Verificar
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <img src="/logo.jpeg" alt="Fuigo" className="mx-auto h-24 w-auto mb-4" />
          <p className="text-lg text-blue-600 font-medium mb-4">
            compara, elige y llega mas rapido
          </p>
          <h2 className="text-3xl font-extrabold text-gray-900">Crear cuenta</h2>
        </div>
        <form onSubmit={handleSubmit} className="mt-8 space-y-6">
          {error && <div className="rounded-md bg-red-50 p-4"><p className="text-sm text-red-800">{error}</p></div>}
          <input
            type="text"
            required
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="appearance-none relative block w-full px-3 py-2 border border-gray-300 rounded-md"
            placeholder="Nombre completo"
          />
          <input
            type="email"
            required
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            inputMode="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value.trim().toLowerCase() })}
            className="appearance-none relative block w-full px-3 py-2 border border-gray-300 rounded-md"
            placeholder="Correo electrónico"
          />
          <input
            type="tel"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            className="appearance-none relative block w-full px-3 py-2 border border-gray-300 rounded-md"
            placeholder="Teléfono (opcional)"
          />
          <div>
            <input
              type="password"
              required
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className="appearance-none relative block w-full px-3 py-2 border border-gray-300 rounded-md"
              placeholder="Contraseña"
            />
            <div className="mt-2 text-xs text-gray-600">
              <p className="font-medium mb-1">La contraseña debe contener:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Mínimo 8 caracteres</li>
                <li>Al menos una mayúscula</li>
                <li>Al menos una minúscula</li>
                <li>Al menos un número</li>
                <li>Al menos un carácter especial (!@#$%^&*)</li>
              </ul>
            </div>
          </div>
          <label className="flex items-start">
            <input
              type="checkbox"
              required
              checked={formData.acceptedTerms}
              onChange={(e) => setFormData({ ...formData, acceptedTerms: e.target.checked })}
              className="mr-2 mt-1"
            />
            <span className="text-sm text-gray-700">
              He leído y acepto la{' '}
              <a
                href="https://www.fuigo.co/privacy-policy.html"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 underline hover:text-blue-800"
              >
                Política de Privacidad
              </a>{' '}
              y el tratamiento de mis datos personales
            </span>
          </label>
          <button
            type="submit"
            disabled={loading}
            className="w-full py-2 px-4 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            {loading ? 'Registrando...' : 'Registrarse'}
          </button>
          <div className="text-center">
            <Link to="/login" className="text-sm text-blue-600 hover:text-blue-800">
              ¿Ya tienes cuenta? Inicia sesión
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
